
import { useState, useEffect, useRef } from "react";
import { ManifestationData, ManifestationStep, ChatMessage as ChatMessageType, SavedAffirmation } from "../types";
import { generateBotResponse, generateUniqueId } from "../utils/conversationLogic";
import { getRandomMessage, stepPrompts, formatAffirmationOptions } from "../utils/manifestationSteps";
import { loadAffirmations, saveAffirmations, clearAllAffirmations } from "../utils/storage";
import { useToast } from "./use-toast";
import { loadGeneratedChatBackdrop, saveGeneratedChatBackdrop } from "../utils/chatBackdropStorage";

export function useManifestation() {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [currentStep, setCurrentStep] = useState<ManifestationStep>("welcome");
  const [affirmationsHydrated, setAffirmationsHydrated] = useState(false);
  const { toast } = useToast();
  
  const [manifestationData, setManifestationData] = useState<ManifestationData>({
    goal: "",
    presentTenseGoal: "",
    feelings: "",
    currentReality: "",
    alignment: "",
    coreFeeling: "",
    totalCycles: 0,
    currentCycle: 0,
    savedAffirmations: [],
    usedVisualizationAngles: [],
    activeBackdrop: undefined,
  });


  // Track if an affirmation was just selected to avoid misinterpreting it as cycle count
  const [selectedAffirmationIndex, setSelectedAffirmationIndex] = useState<number | null>(null);

  const initWelcomeMessages = (affirmations: SavedAffirmation[] = []) => {
    setTimeout(() => {
      const welcomePrompt = stepPrompts["welcome"];
      const welcomeMessage = getRandomMessage(welcomePrompt.message);
      
      let followUpMessage: string;
      if (affirmations.length > 0) {
        followUpMessage = "\n\nHere are your saved affirmations:\n\n" +
          formatAffirmationOptions(affirmations) +
          "\n\nWould you like to use one of these, or create a new one?\n(Type: **existing** or **new**)";
      } else {
        followUpMessage = "\n\n" + getRandomMessage(stepPrompts["welcome"].followUp || ["Would you like to create a new affirmation?\n(Type: new)"]);
      }
      
      const combinedMessage: ChatMessageType = {
        id: generateUniqueId(),
        content: welcomeMessage + followUpMessage,
        isUser: false,
        timestamp: new Date(),
        step: "welcome",
      };
      
      setMessages([combinedMessage]);
    }, 500);
  };

  useEffect(() => {
    const persistAffirmations = async () => {
      if (!affirmationsHydrated) return;
      try {
        await saveAffirmations(manifestationData.savedAffirmations);
      } catch (error) {
        console.error("Failed to save affirmations:", error);
        toast({
          title: "Failed to save your affirmations",
          description: "Your affirmations might not persist when you close the page",
          variant: "destructive"
        });
      }
    };
    
    persistAffirmations();
  }, [manifestationData.savedAffirmations, affirmationsHydrated, toast]);

  useEffect(() => {
    const init = async () => {
      setMessages([]);
      setCurrentStep("welcome");
      setSelectedAffirmationIndex(null);
      
      let savedAffirmations: SavedAffirmation[] = [];
      let savedBackdrop: import("../types").AffirmationBackdrop | undefined;
      try {
        [savedAffirmations, savedBackdrop] = await Promise.all([
          loadAffirmations(),
          loadGeneratedChatBackdrop(),
        ]);
      } catch (error) {
        console.error("Failed to load affirmations on init:", error);
      }
      
      setManifestationData({
        goal: "",
        presentTenseGoal: "",
        feelings: "",
        currentReality: "",
        alignment: "",
        coreFeeling: "",
        totalCycles: 0,
        currentCycle: 0,
        savedAffirmations,
        usedVisualizationAngles: [],
        activeBackdrop: savedBackdrop,
      });
      setAffirmationsHydrated(true);
      
      initWelcomeMessages(savedAffirmations);
    };
    
    init();
  }, []);

  const resetManifestation = (overrideAffirmations?: SavedAffirmation[]) => {
    const savedAffirmations = overrideAffirmations !== undefined ? overrideAffirmations : manifestationData.savedAffirmations;
    
    setMessages([]);
    setCurrentStep("welcome");
    setSelectedAffirmationIndex(null);
    
    setManifestationData({
      goal: "",
      presentTenseGoal: "",
      feelings: "",
      currentReality: "",
      alignment: "",
      coreFeeling: "",
      totalCycles: 0,
      currentCycle: 0,
      savedAffirmations,
      usedVisualizationAngles: [],
      activeBackdrop: undefined,
    });
    
    initWelcomeMessages(savedAffirmations);
  };

  /**
   * Attach or update a backdrop on the active session. If the current
   * presentTenseGoal matches a saved affirmation, persist the backdrop there too.
   */
  const setActiveBackdrop = (backdrop: import("../types").AffirmationBackdrop | undefined) => {
    void saveGeneratedChatBackdrop(backdrop);
    setManifestationData((prev) => {
      const next = { ...prev, activeBackdrop: backdrop };
      if (backdrop && prev.presentTenseGoal) {
        const target = prev.presentTenseGoal.trim().toLowerCase();
        const idx = prev.savedAffirmations.findIndex((a) => a.text.trim().toLowerCase() === target);
        if (idx >= 0) {
          const updated = [...prev.savedAffirmations];
          updated[idx] = { ...updated[idx], backdrop };
          next.savedAffirmations = updated;
        }
      }
      return next;
    });
  };

  /**
   * Auto-hydrate backdrop when the active affirmation matches a saved one.
   */
  useEffect(() => {
    if (manifestationData.activeBackdrop || !manifestationData.presentTenseGoal) return;
    const target = manifestationData.presentTenseGoal.trim().toLowerCase();
    const match = manifestationData.savedAffirmations.find((a) => a.text.trim().toLowerCase() === target);
    if (match?.backdrop) {
      setManifestationData((prev) => ({ ...prev, activeBackdrop: match.backdrop }));
    }
  }, [manifestationData.presentTenseGoal, manifestationData.savedAffirmations, manifestationData.activeBackdrop]);



  const resetAllAffirmations = async () => {
    try {
      await clearAllAffirmations();
      
      // Update local state
      setManifestationData(prev => ({
        ...prev,
        savedAffirmations: []
      }));
      
      toast({
        title: "All affirmations cleared",
        description: "Your affirmation history has been reset",
      });
      
      // Reset the conversation too
      resetManifestation([]);
      
    } catch (error) {
      console.error("Failed to clear affirmations:", error);
      toast({
        title: "Failed to clear affirmations",
        description: "Please try again or refresh the page",
        variant: "destructive"
      });
    }
  };

  const addBotMessage = (content: string, step?: ManifestationStep) => {
    console.log("Adding bot message:", content);
    console.log("Current manifestationData:", manifestationData);
    
    let finalContent = content;
    
    // Make sure we properly replace presentTenseGoal in messages
    if (finalContent.includes("{presentTenseGoal}")) {
      if (manifestationData.presentTenseGoal) {
        finalContent = finalContent.replace(/{presentTenseGoal}/g, manifestationData.presentTenseGoal);
        console.log("Replaced presentTenseGoal with:", manifestationData.presentTenseGoal);
      } else if (manifestationData.savedAffirmations.length > 0) {
        // Fallback to first saved affirmation if no presentTenseGoal
        finalContent = finalContent.replace(/{presentTenseGoal}/g, manifestationData.savedAffirmations[0].text);
        console.log("Used first saved affirmation instead of presentTenseGoal:", manifestationData.savedAffirmations[0].text);
      } else if (manifestationData.goal) {
        finalContent = finalContent.replace(/{presentTenseGoal}/g, manifestationData.goal);
        console.log("Used goal instead of presentTenseGoal:", manifestationData.goal);
      } else {
        finalContent = finalContent.replace(/{presentTenseGoal}/g, "[Your affirmation]");
        console.log("No goal or presentTenseGoal found, using placeholder");
      }
    }
    
    // Replace other placeholders
    if (finalContent.includes("{totalCycles}") && manifestationData.totalCycles) {
      finalContent = finalContent.replace(/{totalCycles}/g, manifestationData.totalCycles.toString());
    }
    
    if (finalContent.includes("{currentCycle}") && manifestationData.currentCycle) {
      finalContent = finalContent.replace(/{currentCycle}/g, manifestationData.currentCycle.toString());
    }
    
    // Replace coreFeeling placeholder
    if (finalContent.includes("{coreFeeling}") && manifestationData.coreFeeling) {
      finalContent = finalContent.replace(/{coreFeeling}/g, manifestationData.coreFeeling);
      console.log("Replaced coreFeeling with:", manifestationData.coreFeeling);
    }
    
    console.log("Final bot message after all replacements:", finalContent);
    
    const newMessage: ChatMessageType = {
      id: generateUniqueId(),
      content: finalContent,
      isUser: false,
      timestamp: new Date(),
      step,
    };
    
    setMessages((prev) => [...prev, newMessage]);
  };

  const addUserMessage = (content: string) => {
    const userMessage: ChatMessageType = {
      id: generateUniqueId(),
      content,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    return userMessage;
  };

  const processUserInput = (input: string) => {
    console.log("Processing user input:", input);
    console.log("Current step:", currentStep);
    console.log("Current data:", manifestationData);
    
    // Handle affirmation selection
    let processedInput = input;
    let isAffirmationSelection = false;
    
    if (currentStep === 'goal-setting' && 
        manifestationData.savedAffirmations.length > 0) {
      const affirmationNumber = parseInt(input.trim());
      if (!isNaN(affirmationNumber) && affirmationNumber > 0 && affirmationNumber <= manifestationData.savedAffirmations.length) {
        // Valid affirmation selection
        const selectedAffirmation = manifestationData.savedAffirmations[affirmationNumber - 1];
        if (selectedAffirmation) {
          console.log("Selected affirmation:", selectedAffirmation.text);
          
          // Store the selected affirmation index to avoid confusion with cycle count
          setSelectedAffirmationIndex(affirmationNumber - 1);
          
          // Store the selected affirmation in the state immediately
          setManifestationData(prev => ({
            ...prev,
            presentTenseGoal: selectedAffirmation.text
          }));
          
          isAffirmationSelection = true;
          processedInput = selectedAffirmation.text; // Pass the actual affirmation text to the bot
        } else {
          console.error("Selected affirmation is undefined at index:", affirmationNumber - 1);
        }
      }
    }

    // Process the input using the bot logic
    const { message, nextStep, updatedData } = generateBotResponse(
      processedInput,
      currentStep,
      manifestationData,
      {
        isAffirmationSelection,
        selectedAffirmationIndex: selectedAffirmationIndex
      }
    );

    console.log("Bot response:", {
      message,
      nextStep,
      currentGoal: updatedData.goal,
      presentTenseGoal: updatedData.presentTenseGoal,
      isAffirmationSelection
    });

    // Preserve the selected affirmation if we just made a selection
    if (isAffirmationSelection && selectedAffirmationIndex !== null) {
      const selectedAffirmation = manifestationData.savedAffirmations[selectedAffirmationIndex];
      if (selectedAffirmation) {
        updatedData.presentTenseGoal = selectedAffirmation.text;
      }
    }

    // Update state
    setCurrentStep(nextStep);
    setManifestationData(updatedData);
    
    return { 
      message, 
      nextStep, 
      updatedData, 
      processedInput,
      isAffirmationSelection
    };
  };

  return {
    messages,
    currentStep,
    manifestationData,
    addBotMessage,
    addUserMessage,
    processUserInput,
    setMessages,
    resetManifestation,
    resetAllAffirmations,
    selectedAffirmationIndex,
    setActiveBackdrop,
  };

}
